<template>
  <div>
    <!-- ⚠️ ต้องบอกให้ชัดว่าค่านี้ออกไปนอกระบบ ไม่ใช่ค่าตั้งภายในเหมือนแท็บอื่น -->
    <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-database-check-outline">
      <span class="text-body-2">
        {{ $t('qaFrPanelNote', { endpoint: 'GET /api/panel/v1/feedback' }) }}
      </span>
    </v-alert>

    <v-alert type="warning" variant="tonal" density="compact" class="mb-3" icon="mdi-key-alert-outline">
      <span class="text-body-2">
        {{ $t('qaFrCodeWarn') }}
      </span>
    </v-alert>

    <v-card class="qa-card-sm mb-3" variant="outlined">
      <v-card-title class="d-flex align-center py-3">
        <span class="text-subtitle-1">{{ $t('qaFrTitle') }}</span>
        <v-spacer />
        <v-chip size="small" variant="tonal" label>
          {{ $t('qaFrEnabledCount', { enabled: enabledCount, total: items.length }) }}
        </v-chip>
      </v-card-title>
      <v-divider />

      <div class="pa-4">
        <div class="qa-reason-scroll">
          <table class="qa-reason-table">
            <thead>
              <tr>
                <th style="width: 56px"></th>
                <th style="width: 180px">{{ $t('qaFrColCode') }}</th>
                <th>{{ $t('qaFrColEn') }}</th>
                <th>{{ $t('qaFrColTh') }}</th>
                <th>{{ $t('qaFrColZh') }}</th>
                <th style="width: 96px" class="text-center">{{ $t('qaFrColEnabled') }}</th>
                <th style="width: 56px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in items" :key="index" :class="{ 'qa-reason-off': !item.enabled }">
                <td>
                  <div class="d-flex flex-column">
                    <v-btn icon="mdi-chevron-up" size="x-small" variant="text" :disabled="index === 0" @click="move(index, -1)" />
                    <v-btn icon="mdi-chevron-down" size="x-small" variant="text" :disabled="index === items.length - 1" @click="move(index, 1)" />
                  </div>
                </td>
                <td>
                  <v-text-field
                    v-model="item.code"
                    variant="outlined"
                    density="compact"
                    hide-details
                    placeholder="wrong_info"
                    :error="duplicateCodes.includes(normalizedCode(item.code))"
                  />
                </td>
                <td><v-text-field v-model="item.labelEn" variant="outlined" density="compact" hide-details placeholder="Wrong info" /></td>
                <td><v-text-field v-model="item.labelTh" variant="outlined" density="compact" hide-details placeholder="ข้อมูลผิด" /></td>
                <td><v-text-field v-model="item.labelZh" variant="outlined" density="compact" hide-details placeholder="信息错误" /></td>
                <td class="text-center">
                  <v-switch v-model="item.enabled" color="primary" density="compact" hide-details inset class="d-inline-flex" />
                </td>
                <td>
                  <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="remove(index)" />
                </td>
              </tr>
              <tr v-if="items.length === 0">
                <td colspan="7" class="text-center text-medium-emphasis py-6">
                  {{ $t('qaFrEmpty') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex align-center ga-2 mt-3">
          <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" :disabled="items.length >= maxItems" @click="add">
            {{ $t('qaFrAdd') }}
          </v-btn>
          <v-btn size="small" variant="text" prepend-icon="mdi-restore" @click="resetToDefault">{{ $t('qaFrReset') }}</v-btn>
          <span class="text-caption text-medium-emphasis">{{ $t('qaFrMax', { n: maxItems }) }}</span>
        </div>

        <v-alert v-if="duplicateCodes.length" type="error" variant="tonal" density="compact" class="mt-3">
          <span class="text-body-2">
            {{ $t('qaFrDupCodes', { codes: duplicateCodes.join(', ') }) }}
          </span>
        </v-alert>

        <v-alert v-if="enabledCount === 0 && items.length > 0" type="warning" variant="tonal" density="compact" class="mt-3">
          <span class="text-body-2">
            {{ $t('qaFrAllOff') }}
          </span>
        </v-alert>
      </div>
    </v-card>

    <!-- ⚠️ ต้องโชว์ของที่ออกไปจริง ไม่ใช่ให้คนเดาจากช่องที่กรอก · กฎ "อังกฤษ / ไทย"
         กับการตัดรายการที่ปิดอยู่ทำงานฝั่ง server ไม่ใช่ฝั่งนี้ -->
    <v-card class="qa-card-sm mb-3" variant="outlined">
      <v-card-title class="py-3">
        <span class="text-subtitle-1">{{ $t('qaFrPreviewTitle') }}</span>
      </v-card-title>
      <v-divider />
      <div class="pa-4">
        <div v-if="!preview.length" class="text-body-2 text-medium-emphasis">{{ $t('qaFrPreviewEmpty') }}</div>
        <div v-else>
          <div class="text-caption text-medium-emphasis mb-2">
            {{ $t('qaFrPreviewNote') }}
          </div>
          <div class="qa-reason-scroll">
            <table class="qa-reason-table">
              <thead>
                <tr>
                  <th style="width: 180px">code</th>
                  <th>label</th>
                  <th style="width: 140px">labelEn</th>
                  <th style="width: 140px">labelTh</th>
                  <th style="width: 140px">labelZh</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in preview" :key="row.code">
                  <td><code>{{ row.code }}</code></td>
                  <td>{{ row.label }}</td>
                  <td class="text-medium-emphasis">{{ row.labelEn || '—' }}</td>
                  <td class="text-medium-emphasis">{{ row.labelTh || '—' }}</td>
                  <td class="text-medium-emphasis">{{ row.labelZh || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-card>

    <div class="d-flex align-center">
      <span v-if="updatedAtText" class="text-caption text-medium-emphasis">
        {{ $t('qaSumLastEdited', { when: updatedAtText }) }}<span v-if="updatedBy"> {{ $t('qaSumLastEditedBy', { who: updatedBy }) }}</span>
      </span>
      <span v-else-if="loaded" class="text-caption text-medium-emphasis">
        {{ $t('qaFrNeverEdited') }}
      </span>
      <v-spacer />
      <v-btn color="primary" variant="flat" :loading="saving" @click="save">{{ $t('qaPreSave') }}</v-btn>
    </div>
  </div>
</template>

<script>
/**
 * M — แท็บ "เหตุผลของการให้คะแนน" (ทะเบียนกลาง 3 ภาษา)
 *
 * ⚠️ บันทึกลงฐานข้อมูลจริง (cc.QaSettings section 'feedback-reasons') ไม่ใช่ localStorage
 *    facade (services/qaConsole/index.js) เหลือทางเดียวคือ API จริง — Q10 ลบชั้นจำลองทั้งชั้นแล้ว
 *    ห้ามเติมทางสำรองที่เขียนลงเครื่องแล้วขึ้นว่าบันทึกสำเร็จกลับเข้ามา
 *
 * ⚠️ ต่างจากทุกแท็บใน QA Settings ตรงที่ค่านี้ **เดินทางออกไปนอกระบบ** — เป็น reasons[]
 *    ของ GET /api/panel/v1/feedback ที่ panel ฝั่ง Salesforce อ่าน · การแก้ code จึงเป็น
 *    การเปลี่ยน contract ไม่ใช่แค่แก้ค่าตั้ง (ดูแบนเนอร์เตือนบนหน้าจอ)
 */
import qaConsole from '@/services/qaConsole';

/** ต้องตรงกับ QaFeedbackReasonSettings.MaxItems ฝั่ง server */
const MAX_ITEMS = 50;

/**
 * ⚠️ ชุดนี้มีไว้ให้ปุ่ม "คืนค่าตั้งต้น" เท่านั้น **ไม่ใช่ค่าที่ใช้ตอนโหลด**
 *    ตอนโหลดใช้สิ่งที่ server ส่งมา (ซึ่งถอยไปค่าตั้งต้นให้เองอยู่แล้วเมื่อไม่มีแถว)
 *    ถ้าเอามาใช้ตอนโหลดด้วย จะกลายเป็นค่าตั้งต้นสองชุดที่เพี้ยนจากกันได้เงียบ ๆ
 */
const DEFAULT_ITEMS = [
  { code: 'off_topic', labelEn: 'Off-topic', labelTh: 'นอกเรื่อง', labelZh: '偏离主题', enabled: true },
  { code: 'wrong_info', labelEn: 'Wrong info', labelTh: 'ข้อมูลผิด', labelZh: '信息错误', enabled: true },
  { code: 'wrong_tone', labelEn: 'Wrong tone', labelTh: 'น้ำเสียงไม่เหมาะ', labelZh: '语气不当', enabled: true },
  { code: 'wrong_language', labelEn: 'Wrong language', labelTh: 'ผิดภาษา', labelZh: '语言错误', enabled: true },
  { code: 'too_late', labelEn: 'Too late', labelTh: 'มาช้าเกินไป', labelZh: '回复太慢', enabled: true },
];

export default {
  name: 'QaFeedbackReasonSettings',
  emits: ['saved'],
  data() {
    return {
      items: [],
      preview: [],
      updatedAt: null,
      updatedBy: null,
      loaded: false,
      saving: false,
      maxItems: MAX_ITEMS,
    };
  },
  computed: {
    enabledCount() {
      return this.items.filter((x) => x.enabled).length;
    },
    /** รหัสที่ซ้ำกัน (ไม่สนตัวพิมพ์ เหมือนฝั่ง server) — เตือนก่อนกดบันทึก ไม่ใช่หลังของหาย */
    duplicateCodes() {
      const seen = new Set();
      const dup = new Set();
      this.items.forEach((x) => {
        const code = this.normalizedCode(x.code);
        if (!code) return;
        if (seen.has(code)) dup.add(code);
        seen.add(code);
      });
      return [...dup];
    },
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    normalizedCode(code) {
      return String(code || '').trim().toLowerCase();
    },
    apply(res) {
      const rows = (res && res.value && res.value.items) || [];
      this.items = rows.map((x) => ({
        code: x.code || '',
        labelEn: x.labelEn || '',
        labelTh: x.labelTh || '',
        labelZh: x.labelZh || '',
        // ⚠️ ต้องเทียบกับ false ตรง ๆ · undefined (แถวเก่าที่ยังไม่มีคีย์นี้) ต้องแปลว่า "เปิด"
        //    ไม่ใช่ "ปิด" ไม่งั้นการอัปเดตเวอร์ชันจะปิดทุกรายการให้เองโดยไม่มีใครสั่ง
        enabled: x.enabled !== false,
      }));
      this.preview = (res && res.preview) || [];
      this.updatedAt = res ? res.updatedAt : null;
      this.updatedBy = res ? res.updatedBy : null;
    },
    async load() {
      try {
        this.apply(await qaConsole.getFeedbackReasons());
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaFrLoadFailed', { msg: err.message }) });
      } finally {
        this.loaded = true;
      }
    },
    add() {
      if (this.items.length >= MAX_ITEMS) return;
      this.items.push({ code: '', labelEn: '', labelTh: '', labelZh: '', enabled: true });
    },
    remove(index) {
      this.items.splice(index, 1);
    },
    move(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.items.length) return;
      const [row] = this.items.splice(index, 1);
      this.items.splice(target, 0, row);
    },
    resetToDefault() {
      this.items = DEFAULT_ITEMS.map((x) => ({ ...x }));
    },
    async save() {
      this.saving = true;
      try {
        // ฝั่ง server normalize ให้ (trim / ตัดรหัสซ้ำ / ตัดแถวไม่มีรหัส / ว่างหมด = ค่าตั้งต้น)
        // เอาค่าที่บันทึกจริงกลับมาแสดง ไม่งั้นหน้าจอจะโชว์สิ่งที่คนกรอกแต่ระบบใช้อีกค่าหนึ่ง
        this.apply(await qaConsole.saveFeedbackReasons({ items: this.items }));
        this.$emit('saved', { ok: true, message: this.$t('qaFrSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ตารางกว้างกว่าจอได้ (6 คอลัมน์ + ช่องกรอก) — ให้เลื่อนในกล่องตัวเอง ไม่ใช่ดันทั้งหน้า */
.qa-reason-scroll {
  overflow-x: auto;
}

.qa-reason-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
}

.qa-reason-table th {
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.7;
  padding: 0 8px 8px;
  white-space: nowrap;
}

.qa-reason-table td {
  padding: 4px 8px;
  vertical-align: middle;
}

/* รายการที่ปิดอยู่ต้องดูต่างจากที่เปิด ไม่งั้นตารางยาว ๆ จะแยกไม่ออกว่าอะไรมีผลจริง */
.qa-reason-off {
  opacity: 0.5;
}
</style>
